import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit3, MapPin, DollarSign, Briefcase, Building, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import type { CurrencyCode } from "@/utils/currency";
import { currencyConfig } from "@/utils/currency";

interface EditTestDetailsDialogProps {
  test: {
    id: string;
    location: string;
    work_arrangement: string;
    work_presence: string;
    description: string;
    key_responsibilities: string;
    salary_range_min: number | null;
    salary_range_max: number | null;
    salary_currency: string;
  };
  canEdit: boolean;
}

export const EditTestDetailsDialog = ({ test, canEdit }: EditTestDetailsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    location: test.location || "",
    work_arrangement: test.work_arrangement || "Full-Time",
    work_presence: test.work_presence || "Fully On-site",
    description: test.description || "",
    key_responsibilities: test.key_responsibilities || "",
    salary_range_min: test.salary_range_min || null,
    salary_range_max: test.salary_range_max || null,
    salary_currency: (test.salary_currency || "INR") as CurrencyCode,
  });

  const handleSave = async () => {
    try {
      setSaving(true);

      const { error } = await supabase
        .from('tests')
        .update({
          location: formData.location,
          work_arrangement: formData.work_arrangement,
          work_presence: formData.work_presence,
          description: formData.description,
          key_responsibilities: formData.key_responsibilities,
          salary_range_min: formData.salary_range_min,
          salary_range_max: formData.salary_range_max,
          salary_currency: formData.salary_currency,
        })
        .eq('id', test.id);

      if (error) throw error;

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['test', test.id] });

      toast({
        title: "Test details updated",
        description: "Your changes have been saved successfully.",
      });

      setOpen(false);
    } catch (error: any) {
      console.error('Error updating test details:', error);
      toast({
        title: "Error updating test",
        description: error.message || "Failed to update test details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    setFormData({
      location: test.location || "",
      work_arrangement: test.work_arrangement || "Full-Time",
      work_presence: test.work_presence || "Fully On-site",
      description: test.description || "",
      key_responsibilities: test.key_responsibilities || "",
      salary_range_min: test.salary_range_min || null,
      salary_range_max: test.salary_range_max || null,
      salary_currency: (test.salary_currency || "INR") as CurrencyCode,
    });
    setOpen(false);
  };

  if (!canEdit) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Edit3 className="h-4 w-4" />
          Edit Details
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Test Details</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Location
            </Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Enter location"
            />
          </div>

          {/* Work Arrangement */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Work Arrangement
            </Label>
            <Select 
              value={formData.work_arrangement} 
              onValueChange={(value) => setFormData({ ...formData, work_arrangement: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select work arrangement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Full-Time">Full-Time</SelectItem>
                <SelectItem value="Part-Time">Part-Time</SelectItem>
                <SelectItem value="Freelance">Freelance</SelectItem>
                <SelectItem value="Contract">Contract</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Work Presence */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              Work Presence
            </Label>
            <Select 
              value={formData.work_presence} 
              onValueChange={(value) => setFormData({ ...formData, work_presence: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select work presence" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Fully On-site">Fully On-site</SelectItem>
                <SelectItem value="Remote">Remote</SelectItem>
                <SelectItem value="Hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Salary Range */}
          <div className="space-y-4">
            <Label className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Salary Range
            </Label>
            
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="salary_min" className="text-sm">Minimum</Label>
                <Input
                  id="salary_min"
                  type="number"
                  value={formData.salary_range_min || ""}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    salary_range_min: e.target.value ? parseInt(e.target.value) : null 
                  })}
                  placeholder="Min salary"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="salary_max" className="text-sm">Maximum</Label>
                <Input
                  id="salary_max"
                  type="number"
                  value={formData.salary_range_max || ""}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    salary_range_max: e.target.value ? parseInt(e.target.value) : null 
                  })}
                  placeholder="Max salary"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm">Currency</Label>
                <Select 
                  value={formData.salary_currency} 
                  onValueChange={(value: CurrencyCode) => setFormData({ ...formData, salary_currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(currencyConfig).map(([code, config]) => (
                      <SelectItem key={code} value={code}>
                        <div className="flex items-center gap-2">
                          <span className="text-base">{config.symbol}</span>
                          <span>{config.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* About the Role */}
          <div className="space-y-2">
            <Label htmlFor="description" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              About the Role
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the role and what the candidate will be doing..."
              rows={4}
            />
          </div>

          {/* Key Responsibilities */}
          <div className="space-y-2">
            <Label htmlFor="key_responsibilities" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Key Responsibilities
            </Label>
            <Textarea
              id="key_responsibilities"
              value={formData.key_responsibilities}
              onChange={(e) => setFormData({ ...formData, key_responsibilities: e.target.value })}
              placeholder="List the main responsibilities for this role..."
              rows={4}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};